import { AxiosError } from 'axios'
import { FC, useState } from 'react'
import { toast } from 'react-toastify'

import { useAcceptOfferMutation } from 'src/reactQuery/useAcceptOfferMutation'
import { useCancelSubscriptionMutation } from 'src/reactQuery/useCancelSubscriptionMutation'
import { useCreateCheckoutMutation } from 'src/reactQuery/useCreateCheckoutMutation'
import { useCreatePortalMutation } from 'src/reactQuery/useCreatePortalMutation'
import { useGetSubscriptionPricesQuery } from 'src/reactQuery/useGetSubscriptionPricesQuery'
import { useGetSubscriptionStatusQuery } from 'src/reactQuery/useGetSubscriptionStatusQuery'
import { useResumeSubscriptionMutation } from 'src/reactQuery/useResumeSubscriptionMutation'
import {
  ApiErrorResponse,
  SubscriptionPlanType,
  SubscriptionStatus,
} from 'src/services/subscriptionApi/interfaces/subscriptionApi.interface'
import { getPlanDisplayName } from 'src/utils/getPlanDisplayName'

import OfferCard from './OfferCard'
import { PaymentFailedBanner } from './PaymentFailedBanner'
import SubscriptionCards from './SubscriptionCards'
import styles from './subscription.module.scss'

const planTypeToId: Record<SubscriptionPlanType, string | null> = {
  custom: null,
  daily: 'daily-data-fetch',
  monthly: 'monthly-data-fetch',
  none: null,
  weekly: 'weekly-data-fetch',
}

const formatDate = (dateString?: string) => {
  if (!dateString) return null
  return new Date(dateString).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const Subscription: FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'annually' | 'monthly'>(
    'monthly'
  )
  const [promoCode, setPromoCode] = useState<string>('')
  const [isPromoApplied, setIsPromoApplied] = useState<boolean>(false)
  const [isPromoInvalid, setIsPromoInvalid] = useState<boolean>(false)

  // Fetch subscription prices and status
  const { data: pricesData, isLoading: isPricesLoading } =
    useGetSubscriptionPricesQuery()
  const { data: statusData, isLoading: isStatusLoading } =
    useGetSubscriptionStatusQuery()

  const isLoading = isPricesLoading || isStatusLoading

  // Mutations for Stripe Checkout and Portal
  const checkoutMutation = useCreateCheckoutMutation()
  const portalMutation = useCreatePortalMutation()
  // Inline cancel / resume (no portal round-trip)
  const cancelMutation = useCancelSubscriptionMutation()
  const resumeMutation = useResumeSubscriptionMutation()
  // Accept a custom plan the admin offered
  const acceptOfferMutation = useAcceptOfferMutation()

  // Statuses for which the customer has a live, billable Stripe subscription the
  // portal can manage. Mirrors the backend ACTIVE_SUBSCRIPTION_STATUSES exactly.
  // 'incomplete'/'incomplete_expired' are intentionally excluded: they are an
  // abandoned/pending checkout that never billed and auto-expires, so the user
  // must be able to (re)subscribe via Checkout rather than be sent to the Portal.
  const LIVE_STATUSES: SubscriptionStatus[] = [
    'active',
    'trialing',
    'past_due',
    'unpaid',
  ]

  const subscriptionStatus = statusData?.data
  const subscription = subscriptionStatus?.subscription ?? null
  const pendingOffer = subscriptionStatus?.pendingOffer ?? null
  const currentPlan = subscriptionStatus?.plan || 'none'
  const status = subscriptionStatus?.status
  // trialing is treated as active-ish: show the plan + Manage Billing.
  const isActive = status === 'active' || status === 'trialing'
  const isCanceled = status === 'canceled'
  // Subscription is live but set to end at the period boundary.
  const isCanceling = !!subscription?.cancelAtPeriodEnd
  const isMutatingPlan = cancelMutation.isPending || resumeMutation.isPending
  const hasPaymentFailed = status === 'past_due'
  const isLive = !!status && LIVE_STATUSES.includes(status)
  const hasManageableSubscription = isLive || isCanceled
  // Manually granted (offline / non-Stripe) plans have no Stripe subscription,
  // so there is nothing for the Stripe billing portal to manage. Hide billing
  // controls for them instead of letting Manage Billing error out.
  const hasStripeBilling = !!subscription?.stripeSubscriptionId
  const activePlanId = hasManageableSubscription
    ? planTypeToId[currentPlan]
    : null

  const VALID_PROMO_CODE = 'Hapttic20'

  const handleApplyPromo = () => {
    if (promoCode.trim().toLowerCase() === VALID_PROMO_CODE.toLowerCase()) {
      setIsPromoApplied(true)
      setIsPromoInvalid(false)
    } else if (promoCode.trim()) {
      setIsPromoInvalid(true)
      setIsPromoApplied(false)
    }
  }

  const handleRemovePromo = () => {
    setIsPromoApplied(false)
    setIsPromoInvalid(false)
    setPromoCode('')
  }

  const handleManageBilling = () => {
    portalMutation.mutate(undefined, {
      onError: () => {
        toast.error('Could not open the billing portal. Please try again.')
      },
      onSuccess: (response) => {
        if (response.data?.url) {
          window.location.href = response.data.url
        } else {
          toast.error('Could not open the billing portal. Please try again.')
        }
      },
    })
  }

  const handleAcceptOffer = () => {
    acceptOfferMutation.mutate(undefined, {
      onError: (error) => {
        const data = (error as AxiosError<ApiErrorResponse>).response?.data
        toast.error(
          data?.error || 'Could not start checkout. Please try again.'
        )
      },
      onSuccess: (response) => {
        if (response.data?.url) {
          window.location.href = response.data.url
        } else {
          toast.error('Could not start checkout. Please try again.')
        }
      },
    })
  }

  const handleCancelPlan = () => {
    const confirmed = window.confirm(
      'Cancel your subscription? You will keep access until the end of the current billing period.'
    )
    if (!confirmed) return
    cancelMutation.mutate(
      {},
      {
        onError: () => {
          toast.error('Could not cancel the subscription. Please try again.')
        },
        onSuccess: (response) => {
          const cancelDate = formatDate(response.data?.cancelAt)
          toast.success(
            response.data?.message ||
              (cancelDate
                ? `Your subscription will cancel on ${cancelDate}.`
                : 'Your subscription will cancel at the end of the period.')
          )
        },
      }
    )
  }

  const handleResumePlan = () => {
    resumeMutation.mutate(undefined, {
      onError: () => {
        toast.error('Could not resume the subscription. Please try again.')
      },
      onSuccess: (response) => {
        toast.success(
          response.data?.message || 'Your subscription has been resumed.'
        )
      },
    })
  }

  const handleGetPlan = (_planId: string, priceId?: string) => {
    if (!priceId) return

    // If the customer already has a live/manageable Stripe subscription
    // (active/trialing/past_due/unpaid/canceled), send them to the Portal for
    // plan changes. An 'incomplete' sub is NOT manageable here: it falls through
    // to Checkout so the user can complete payment / (re)subscribe.
    if (hasManageableSubscription) {
      handleManageBilling()
      return
    }

    // New subscription (or abandoned 'incomplete') - use Checkout.
    // When a promo is applied, thread the code through; the backend re-validates
    // it and is the source of truth for the actual discount.
    checkoutMutation.mutate(
      { priceId, promoCode: isPromoApplied ? VALID_PROMO_CODE : undefined },
      {
        onError: () => {
          toast.error('Could not start checkout. Please try again.')
        },
        onSuccess: (response) => {
          if (response.data?.url) {
            window.location.href = response.data.url
          } else {
            toast.error('Could not start checkout. Please try again.')
          }
        },
      }
    )
  }

  const planDisplayName = isLoading
    ? 'Loading...'
    : getPlanDisplayName(currentPlan)

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      currency: currency.toUpperCase(),
      style: 'currency',
    }).format(amount / 100)
  }

  const getPlanSubtext = () => {
    if (isLoading) {
      return 'Fetching subscription...'
    }

    if (!subscriptionStatus || currentPlan === 'none') {
      return 'No active plan'
    }

    const { upcomingPayment } = subscriptionStatus
    const periodEndDate = upcomingPayment
      ? formatDate(upcomingPayment.date)
      : formatDate(subscription?.currentPeriodEnd)

    if (subscription?.cancelAtPeriodEnd && periodEndDate) {
      return `Cancels on ${periodEndDate}`
    }

    if (isCanceled) {
      return 'Subscription ended'
    }

    // Manually granted custom plans (no Stripe subscription behind them) are
    // billed out-of-band, so we hide the Stripe renewal date/amount and show
    // the manual-billing note instead. Custom plans billed through Stripe keep
    // the normal renewal amount/date.
    if (isActive && currentPlan === 'custom' && !hasStripeBilling) {
      return 'Payable upon invoice'
    }

    if (isActive && upcomingPayment && periodEndDate) {
      const amount = formatAmount(
        upcomingPayment.amount,
        upcomingPayment.currency
      )
      return `${amount} on ${periodEndDate}`
    }

    if (isActive && periodEndDate) {
      return `Renews on ${periodEndDate}`
    }

    return `${subscription?.billingInterval || 'monthly'} billing`
  }

  return (
    <div className={styles.subscription}>
      {/* Payment Failed Banner - Only shown when payment fails */}
      {hasPaymentFailed && (
        <PaymentFailedBanner
          onPayNow={handleManageBilling}
          onUpdateCard={handleManageBilling}
        />
      )}

      {/* Header */}
      <div className={styles.header}>
        {/* Left side - Plan info */}
        <div className={styles.planInfo}>
          <div className={styles.planTextContainer}>
            <span className={styles.planTitle}>Your Plan</span>
            <span className={styles.planSubtext}>{getPlanSubtext()}</span>
          </div>
          <div className={styles.planBadge}>{planDisplayName}</div>
          {hasManageableSubscription && hasStripeBilling && (
            <button
              className={styles.manageBillingButton}
              disabled={portalMutation.isPending}
              onClick={handleManageBilling}
              type="button"
            >
              {portalMutation.isPending ? 'Loading...' : 'Manage Billing'}
            </button>
          )}
        </div>

        {/* Right side - Billing toggle */}
        <div className={styles.billingToggle}>
          <button
            className={`${styles.toggleButton} ${billingPeriod === 'monthly' ? styles.toggleButtonActive : ''}`}
            onClick={() => setBillingPeriod('monthly')}
            type="button"
          >
            Monthly
          </button>
          <button
            className={`${styles.toggleButton} ${billingPeriod === 'annually' ? styles.toggleButtonActive : ''}`}
            onClick={() => setBillingPeriod('annually')}
            type="button"
          >
            Annually
          </button>
        </div>
      </div>

      {/* Custom/base plan offered by the admin — shown as a card; accept via Checkout */}
      {pendingOffer && (
        <OfferCard
          amount={pendingOffer.unitAmount}
          currency={pendingOffer.currency}
          interval={pendingOffer.interval}
          isProcessing={acceptOfferMutation.isPending}
          onSubscribe={handleAcceptOffer}
          productName={pendingOffer.productName}
        />
      )}

      {/* Cards container */}
      <SubscriptionCards
        activePlanId={activePlanId}
        billingPeriod={billingPeriod}
        cancelDate={
          isCanceling ? formatDate(subscription?.currentPeriodEnd) : null
        }
        isCanceling={isCanceling}
        isMutatingPlan={isMutatingPlan}
        isProcessing={checkoutMutation.isPending || portalMutation.isPending}
        isPromoApplied={isPromoApplied}
        onCancelPlan={handleCancelPlan}
        onGetPlan={handleGetPlan}
        onResumePlan={handleResumePlan}
        pricesData={pricesData}
      />

      {/* Footer - Promo Code */}
      <div className={styles.footer}>
        <span className={styles.promoLabel}>Do you have a Promo Code?</span>
        <div className={styles.promoRow}>
          <div className={styles.promoInputContainer}>
            <input
              className={`${styles.promoInput} ${isPromoApplied ? styles.promoInputApplied : ''}`}
              disabled={isPromoApplied}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="Promo Code"
              type="text"
              value={promoCode}
            />
            {isPromoApplied ? (
              <div className={styles.promoAppliedContainer}>
                <span className={styles.promoAppliedBadge}>
                  Applied
                  <svg
                    fill="none"
                    height="16"
                    viewBox="0 0 16 16"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM6.5 11.5L3 8L4.41 6.59L6.5 8.67L11.09 4.09L12.5 5.5L6.5 11.5Z"
                      fill="#8080FF"
                    />
                  </svg>
                </span>
                <button
                  className={styles.promoRemoveButton}
                  onClick={handleRemovePromo}
                  type="button"
                >
                  <svg
                    fill="none"
                    height="12"
                    viewBox="0 0 12 12"
                    width="12"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L11 11M1 11L11 1"
                      stroke="#25262D"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>
            ) : isPromoInvalid ? (
              <div className={styles.promoAppliedContainer}>
                <span className={styles.promoInvalidBadge}>
                  Invalid
                  <svg
                    fill="none"
                    height="16"
                    viewBox="0 0 16 16"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM9 13H7V11H9V13ZM9 9H7V3H9V9Z"
                      fill="#B42318"
                    />
                  </svg>
                </span>
                <button
                  className={styles.promoRemoveButton}
                  onClick={handleRemovePromo}
                  type="button"
                >
                  <svg
                    fill="none"
                    height="12"
                    viewBox="0 0 12 12"
                    width="12"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L11 11M1 11L11 1"
                      stroke="#25262D"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                className={styles.promoButton}
                onClick={handleApplyPromo}
                type="button"
              >
                Apply
              </button>
            )}
          </div>

          {isPromoApplied && (
            <div className={styles.promoSuccessMessage}>
              <div className={styles.promoSuccessIcon}>
                <svg
                  fill="none"
                  height="20"
                  viewBox="0 0 20 20"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8.125 14.375L3.75 10L5.5125 8.2375L8.125 10.8375L13.8625 5.1125L15.625 6.875L8.125 14.375Z"
                    fill="#15881C"
                  />
                </svg>
              </div>
              <span>Promo Code applied! Prices have been updated.</span>
            </div>
          )}

          {isPromoInvalid && (
            <div className={styles.promoErrorMessage}>
              <div className={styles.promoErrorIcon}>
                <svg
                  fill="none"
                  height="20"
                  viewBox="0 0 20 20"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z"
                    fill="#B42318"
                  />
                </svg>
              </div>
              <span>Invalid promo code. Please try again.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Subscription
